import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Contact,
  durationStringToMinutes,
  FormFieldType,
  MeetingLocationType,
  NotificationType,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { formatInTimeZone } from 'date-fns-tz';
import { ServiceEventType } from '@vendasta/meetings';
import { TimeSlot } from '../bookme/bookme';
import { first } from 'rxjs/operators';
import { parsePhoneNumberToPhoneFields } from '../util';
import { resolveBookingSource } from '../booking-request-id';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { customEmailValidator } from '../bookme-contact-form/bookme-contact-form.component';
import { CartItem } from '../shared/booking-cart/booking-cart.component';
import { MeetingForm } from '@vendasta/meetings';
import { DynamicFormField } from '../shared/dynamic-form/dynamic-form.interfaces';
import { buildDynamicFormField } from '../shared/dynamic-form/dynamic-form.mapper';
import { PhoneInputComponent } from '../shared/phone-input/phone-input.component';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
} from '../analytics/constants';

@Component({
  selector: 'app-confirm-booking-page',
  standalone: false,
  templateUrl: './confirm-booking-page.component.html',
  styleUrls: ['./confirm-booking-page.component.scss'],
})
export class ConfirmBookingPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly recaptchaV3Service = inject(ReCaptchaV3Service);
  private readonly analytics = inject(ProductAnalyticsService);

  cartItems = signal<CartItem[]>([]);
  submitting = false;
  submissionError = signal('');
  isSingleEventRoute = false;

  scheduledDate = signal('');
  scheduledTime = signal('');

  /** Form controls */
  form = new UntypedFormGroup({
    firstName: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/\S/),
    ]),
    lastName: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/\S/),
    ]),
    email: new UntypedFormControl(''),
    phoneNumber: new UntypedFormControl(''),
    location: new UntypedFormControl(''),
    comment: new UntypedFormControl(''),
  });

  get phoneNumberControl(): FormControl {
    return this.form.get('phoneNumber') as FormControl;
  }

  /** Guest emails */
  showGuestEmails = false;
  guestEmails: string[] = [];
  separatorKeyCodes = [ENTER, COMMA];

  /** Custom form fields from selected event types */
  customFormFields: DynamicFormField[] = [];

  get customFieldsInvalid(): boolean {
    return this.customFormFields.some((field) => field.formControl.invalid);
  }

  /** Field visibility and required state derived from selected events */
  isEmailRequired = false;
  isPhoneRequired = false;
  showSmsNotice = false;
  showLocationField = false;
  locationInstructions = '';

  @ViewChild('guestEmailInput') guestEmailInput: ElementRef<HTMLInputElement>;
  @ViewChild(PhoneInputComponent) phoneInput: PhoneInputComponent;

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    this.isSingleEventRoute =
      !!params['calendarId'] && !!params['meetingTypeId'];

    const initialDraft = this.storeService.getCustomerInfoDraft();
    if (initialDraft) {
      this.form.patchValue(initialDraft.formValue);
      this.guestEmails = [...initialDraft.guestEmails];
      this.showGuestEmails = initialDraft.showGuestEmails;
    }
    let pendingCustomAnswers: Record<string, unknown> | null =
      initialDraft?.customAnswers ?? null;

    this.storeService.selectedBookingEvents$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((events) => {
        this.cartItems.set(
          events.map((event) => ({
            name: event.name,
            providerName: this.storeService.getProviderNameForEvent(event),
            duration: event.duration
              ? durationStringToMinutes({ duration: event.duration })
              : 0,
            initials: this.getInitials(event.name),
          }))
        );

        this.customFormFields = this.extractCustomFormFields(events);
        this.deriveFieldRequirements(events);
        if (pendingCustomAnswers) {
          for (const field of this.customFormFields) {
            const saved = pendingCustomAnswers[field.id];
            if (saved !== undefined) {
              field.formControl.setValue(saved);
            }
          }
          pendingCustomAnswers = null;
        }
      });

    // Read time slot from store (immediate) and subscribe for updates
    const initialSlot = this.storeService.getSelectedTimeSlot();
    if (initialSlot?.start) {
      this.updateScheduleDisplay(initialSlot);
    }
    this.storeService.timeSlot$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((timeSlot) => {
        if (timeSlot?.start) {
          this.updateScheduleDisplay(timeSlot);
        }
      });
  }

  onRemoveCartItem(index: number): void {
    const events = this.storeService.getSelectedBookingEvents();
    if (!events[index]) {
      return;
    }
    this.storeService.setSelectedBookingEvents(
      events.filter((_, i) => i !== index)
    );
  }

  toggleGuestEmails(): void {
    this.showGuestEmails = !this.showGuestEmails;
  }

  addGuestEmail(value: string): void {
    const email = value?.trim();
    if (
      email &&
      this.isValidEmail(email) &&
      !this.guestEmails.includes(email)
    ) {
      this.guestEmails = [...this.guestEmails, email];
    }
    if (this.guestEmailInput?.nativeElement) {
      this.guestEmailInput.nativeElement.value = '';
    }
  }

  removeGuestEmail(email: string): void {
    this.guestEmails = this.guestEmails.filter((e) => e !== email);
  }

  clearAllGuestEmails(): void {
    this.guestEmails = [];
    if (this.guestEmailInput?.nativeElement) {
      this.guestEmailInput.nativeElement.value = '';
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  }

  onScheduleAppointment(): void {
    if (this.submitting) {
      return;
    }
    if (this.form.invalid || this.customFieldsInvalid) {
      this.form.markAllAsTouched();
      this.customFormFields.forEach((field) => field.formControl.markAsTouched());
      return;
    }

    this.submitting = true;
    this.submissionError.set('');
    const events = this.storeService.getSelectedBookingEvents();
    const timeSlot = this.storeService.getSelectedTimeSlot();

    if (!timeSlot?.start || events.length === 0) {
      this.submitting = false;
      return;
    }

    this.recaptchaV3Service
      .execute('bookMeeting')
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (recaptchaToken) => {
          this.submitBooking(events, timeSlot.start, recaptchaToken);
        },
        error: () => {
          this.submitting = false;
        },
      });
  }

  private submitBooking(
    events: ServiceEventType[],
    start: Date,
    recaptchaToken: string
  ): void {
    const formValue = this.form.value;
    const selectedHost = this.storeService.getSelectedHost();
    const eventHostMap = this.storeService.getEventHostMap();

    const targets = events.map((event, index) => {
      // Only CLIENT_CHOICE requires a specific host userId; other types (ROUND_ROBIN, PRIORITY_ROUND_ROBIN, MULTI_HOST) are handled server-side
      const isClientChoice = event.eventType === TeamEventMeetingType.CLIENT_CHOICE;
      const hostId = eventHostMap.get(event.id) || selectedHost || undefined;
      return {
        eventTypeId: event.id,
        userId: isClientChoice ? hostId : undefined,
        order: index,
      };
    });

    const phoneFields = parsePhoneNumberToPhoneFields(
      this.formatPhoneNumber(formValue.phoneNumber)
    );

    const attendees = [
      new Contact({
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: formValue.email,
        phoneFields,
        timeZone: { id: this.storeService.getTimezone() },
        isPrimary: true,
      }),
      ...this.guestEmails.map(
        (email) => new Contact({ email, isPrimary: false })
      ),
    ];

    // Build custom form answers, preserving each field's original meeting-type
    // type (so DROPDOWN/MULTIPLE_CHOICE aren't collapsed to SELECT).
    const formAnswers: MeetingForm | undefined =
      this.customFormFields.length > 0
        ? {
            formAnswers: this.customFormFields.map((field) => ({
              id: field.id,
              label: field.label,
              type: field.originalType ?? FormFieldType.FORM_FIELD_TYPE_TEXT,
              answer: Array.isArray(field.formControl.value)
                ? field.formControl.value
                : [field.formControl.value],
            })),
          }
        : undefined;

    const bookingOriginId =
      this.storeService.getBookingOriginId() || events[0]?.id || '';

    const isMultiService = events.length > 1;
    const eventTypeIds = events.map((e) => e.id);
    const totalDurationMinutes = events.reduce(
      (sum, e) =>
        sum +
        (e.duration ? durationStringToMinutes({ duration: e.duration }) : 0),
      0
    );

    this.storeService
      .bookBatchMeeting({
        targets,
        start,
        attendees,
        comment: formValue.comment,
        location: formValue.location,
        formAnswers,
        recaptchaToken,
        bookingSource: resolveBookingSource(),
        bookingOriginId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bookingGroupId) => {
          this.submitting = false;
          this.storeService.clearCustomerInfoDraft();
          if (isMultiService) {
            this.analytics.trackEvent(
              POSTHOG_KEYS.MULTI_SERVICE_BOOKING_CONFIRMED,
              POSTHOG_CATEGORIES.MULTI_SERVICE_BOOKING,
              POSTHOG_ACTIONS.CHECKOUT,
              events.length,
              {
                cartSize: events.length,
                eventTypeIds,
                totalDurationMinutes,
              }
            );
          }
          this.router.navigate(['/meeting-info', bookingGroupId], {
            queryParamsHandling: 'merge',
          });
        },
        error: (err) => {
          this.submitting = false;
          if (err.status === 409) {
            this.storeService.setErrorDisplay('conflict-booking');
            return;
          }
          const message = (
            err?.error?.message ||
            err?.message ||
            ''
          ).toLowerCase();
          const isTerminal =
            message.includes('no longer exists') ||
            message.includes('expired') ||
            message.includes('host calendar/meeting app link is not connected');
          if (isTerminal) {
            this.storeService.handleMeetingTypeError(err);
          } else {
            this.submissionError.set(
              $localize`:@@BOOK.SUBMISSION_ERROR:Something went wrong while submitting your booking. Please try again.`
            );
          }
        },
      });
  }

  onBack(): void {
    this.saveFormDraft();
    this.storeService.setBookingStep('datetime');
  }

  onMakeChanges(): void {
    this.saveFormDraft();
    if (this.isSingleEventRoute) {
      const providerNeeded = this.storeService.getShouldShowProviderStep() && !this.storeService.getProviderStepSkipped();
      const step = providerNeeded ? 'provider' : 'datetime';
      this.storeService.setBookingStep(step);
    } else {
      this.storeService.setBookingStep('scheduling');
    }
  }

  private saveFormDraft(): void {
    const customAnswers: Record<string, unknown> = {};
    for (const field of this.customFormFields) {
      customAnswers[field.id] = field.formControl.value;
    }
    this.storeService.setCustomerInfoDraft({
      formValue: this.form.value,
      guestEmails: [...this.guestEmails],
      showGuestEmails: this.showGuestEmails,
      customAnswers,
    });
  }

  private deriveFieldRequirements(events: ServiceEventType[]): void {
    const storeState = this.storeService.getCurrentState();
    const meetingTypePhoneRequired =
      storeState?.meetingType?.isPhoneNumberRequired === true;
    const meetingTypeEmailRequired =
      storeState?.meetingType?.isEmailRequired === true;

    // Email is required when the meeting type requests it, or whenever any
    // selected event sends an email notification (EMAIL, BOTH, or unset).
    this.isEmailRequired =
      meetingTypeEmailRequired ||
      events.some(
        (e) =>
          e.notificationType === undefined ||
          e.notificationType === null ||
          e.notificationType === NotificationType.EMAIL ||
          e.notificationType === NotificationType.BOTH
      );

    // Phone is required if ANY selected event has smsRequired or notificationType is SMS/BOTH
    this.isPhoneRequired =
      meetingTypePhoneRequired ||
      events.some(
        (e) =>
          e.smsRequired ||
          e.notificationType === NotificationType.SMS ||
          e.notificationType === NotificationType.BOTH
      );

    // SMS consent disclosure is only shown when notification channel is SMS
    // (or BOTH). Email-only bookings never show the banner, even when the
    // phone field is also required.
    this.showSmsNotice = events.some(
      (e) =>
        e.notificationType === NotificationType.SMS ||
        e.notificationType === NotificationType.BOTH
    );

    this.showLocationField = events.some(
      (e) => e.locationType === MeetingLocationType.IN_PERSON_CLIENT_SITE
    );

    const clientSiteEvent = events.find(
      (e) =>
        e.locationType === MeetingLocationType.IN_PERSON_CLIENT_SITE &&
        !!e.locationGuideline?.trim()
    );
    this.locationInstructions =
      clientSiteEvent?.locationGuideline?.trim() ?? '';

    // Update email validators
    const emailControl = this.form.get('email');
    if (this.isEmailRequired) {
      emailControl.setValidators([Validators.required, customEmailValidator()]);
    } else {
      emailControl.setValidators([customEmailValidator()]);
    }
    emailControl.updateValueAndValidity();

    // Update phone validators
    const phoneControl = this.form.get('phoneNumber');
    phoneControl.setValidators(
      this.isPhoneRequired ? [Validators.required] : []
    );
    phoneControl.updateValueAndValidity();

    // Update location validators
    const locationControl = this.form.get('location');
    if (this.showLocationField) {
      locationControl.setValidators([
        Validators.required,
        Validators.pattern(/\S/),
      ]);
    } else {
      locationControl.clearValidators();
    }
    locationControl.updateValueAndValidity();
  }

  private formatPhoneNumber(
    rawPhone: string | null | undefined
  ): string | undefined {
    if (!rawPhone || !this.phoneInput) {
      return undefined;
    }
    const countryCallingCode =
      this.phoneInput.countryDetails.countryCallingCode.toString();
    const countryCode = this.phoneInput.countryDetails.countryCode;
    let phoneNumber = rawPhone.toString().replace(/\D/g, '');
    if (phoneNumber.startsWith(countryCallingCode)) {
      phoneNumber = phoneNumber.substring(countryCallingCode.length);
    }
    return `${countryCallingCode}-${phoneNumber}-${countryCode}`;
  }

  private updateScheduleDisplay(timeSlot: TimeSlot): void {
    const tz =
      this.storeService.getTimezone() ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startTime = formatInTimeZone(timeSlot.start, tz, 'h:mm a').toUpperCase();
    const endTime = timeSlot.end ? formatInTimeZone(timeSlot.end, tz, 'h:mm a').toUpperCase() : null;
    this.scheduledDate.set(formatInTimeZone(timeSlot.start, tz, 'EEEE, MMM d'));
    this.scheduledTime.set(endTime ? `${startTime} to ${endTime}` : startTime);
  }

  private getInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  private extractCustomFormFields(events: ServiceEventType[]): DynamicFormField[] {
    const fields: DynamicFormField[] = [];
    const seenFieldIds = new Set<string>();

    for (const event of events) {
      if (!event.serviceForm?.fields?.length) {
        continue;
      }

      for (const field of event.serviceForm.fields) {
        if (!field.id || seenFieldIds.has(field.id) || field.type === FormFieldType.FORM_FIELD_TYPE_INVALID) {
          continue;
        }

        seenFieldIds.add(field.id);
        const base = field.required ? [Validators.required, Validators.pattern(/\S/)] : [];

        const selectOptions =
          field.type === FormFieldType.FORM_FIELD_TYPE_SELECT
            ? 'config' in field && (field as Record<string, unknown>).config
              ? ((field as Record<string, unknown>).config as { options?: { text: string; value: string }[] })
                  ?.options || []
              : []
            : [];

        const built = buildDynamicFormField(
          { id: field.id, label: field.label || '', required: field.required || false, type: field.type, options: field.options },
          base,
          { selectOptions, defaultValue: '', defaultArrayValue: [] }
        );
        if (built) {
          fields.push(built);
        }
      }
    }

    return fields;
  }
}
